﻿let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  FlatList,
  ActivityIndicator,
  Alert,
  Modal,
  Dimensions,
  Animated,
  RefreshControl,
  Image,
  Linking, // Added Linking for external application
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useProfessional } from '../../context/ProfessionalContext';
import { useProfessionalOnboarding } from '../../context/ProfessionalOnboardingContext';
import { useGeneral } from '../../context/GeneralContext';

// ⬇️ Import Unified API Functions
import { 


  searchAllJobs, 
  getAPIStatus, 
  getConfiguredAPIs,
  getJobTypeStatistics 
} from '../../services/unifiedJobAPI';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

/**
 * Helper: Transforms a job from the unifiedJobAPI into the structure
 * expected by the RemoteJobCard component.
 */
const transformApiJob = (job) => {
  // Extract simple perks (faked based on job data, as API doesn't provide this detail)
  const descLower = job.description?.toLowerCase() || '';
  const titleLower = job.title?.toLowerCase() || '';

  return {
    id: job.id || job.jobId,
    jobId: job.jobId || job.id,
    title: job.title,
    company: job.company || job.client || 'Company',
    companyLogo: job.logo || job.clientLogo || `https://logo.clearbit.com/${job.company?.toLowerCase().replace(/\s/g, '')}.com`,
    location: job.location,
    salary: job.salary?.budget || 'Competitive',
    type: job.jobType || 'Full-time',
    remote: true,
    isRemote: true,
    posted: job.posted || 'Recently',
    description: job.description,
    requirements: job.requirements || [],
    skills: job.skills || [],
    matchScore: job.matchScore || 80,
    featured: job.featured,
    urgent: job.urgent,
    dataSource: job.dataSource,
    applyLink: job.applyLink,
    
    // Remote specific UI fields
    remotePerks: {
      timeZone: titleLower.includes('europe') || titleLower.includes('eu') ? 'EU Timezones' : 'Flexible',
      communication: descLower.includes('async') ? 'Async-first' : 'Mixed',
      teamSize: job.companySize || (Math.random() > 0.5 ? 'Small (5-15)' : 'Medium (15-30)'),
    },
    companyInfo: {
      size: job.companySize || 'N/A',
      industry: 'Technology/Engineering',
      founded: 'N/A',
      remoteFirst: String(job.location ?? '').toLowerCase() === 'worldwide' || String(job.location ?? '').toLowerCase() === 'global',
    },
  };
};

// --- Component Start ---
export default function RemoteJobsScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_remotejobsscreen`;
    apiFetch('/api/data/jobs?is_remote=true&sort=created_at&order=desc&limit=50').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // Context hooks
  const { 
    userData = {}, 
    savedJobs = [], 
    jobApplications = [], 
    saveJob, 
    unsaveJob, 
    applyToJob, // Corrected function name based on context
    trackUserAction 
  } = useProfessional() || {};
  
  const { 
    profileData = {}, 
    scoring = {}, 
    aiMatching = {},
    runAIProfileAnalysis,
    calculateJobMatch
  } = useProfessionalOnboarding() || {};
  
  const { showToast, trackUserAction: trackGeneralAction } = useGeneral() || {};

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true); // Start loading immediately
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [applyingJobId, setApplyingJobId] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    timeZone: 'all',
    communication: 'all',
    teamSize: 'all',
    experience: 'all',
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Remote job tabs
  const remoteTabs = [
    { id: 'all', title: 'All Remote', icon: 'globe-outline' },
    { id: 'recommended', title: 'For You', icon: 'star-outline' },
    { id: 'saved', title: 'Saved', icon: 'bookmark-outline' },
    { id: 'applied', title: 'Applied', icon: 'checkmark-circle-outline' },
    { id: 'recent', title: 'Recent', icon: 'time-outline' },
  ];

  // ⬇️ Data states (Removed mock data)
  const [allRemoteJobs, setAllRemoteJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);

  // --- API Loading Logic ---

  /**
   * Fetches data from the Unified API, ensuring remote filter is set.
   */
  // File: RemoteJobsScreen.js (Inside loadJobsFromAPI function)

// ... (Around line 116)
  const loadJobsFromAPI = async () => {
    try {
      console.log('🔍 Fetching Remote Engineering Jobs...');
      
      const configuredAPIs = getConfiguredAPIs();
      if (configuredAPIs.total === 0) {
        Alert.alert('Configuration Error', 'No job search APIs are configured.');
        return { jobs: [], sources: {} };
      }
      
      // ✅ FIX: Use the profile's job title or fall back to 'remote engineer'
      const userJobTitle = profileData?.personal?.jobTitle;
      const finalQuery = userJobTitle && userJobTitle.trim() !== '' 
        ? `remote ${userJobTitle}` 
        : 'remote engineer OR remote developer'; // Ensure the search is still remote-focused
      
      // Search with the profile-driven query
      const result = await searchAllJobs({
        query: finalQuery, // <-- Updated to use profile data
        location: profileData?.personal?.location || 'United Kingdom',
        remote: true, // CRITICAL: Keep this hardcoded for the RemoteJobsScreen
        page: 1, 
      });

// ... (rest of the function remains the same)

      if (result.success) {
        const transformedJobs = result.jobs.map(transformApiJob);
        return {
          jobs: transformedJobs,
          sources: result.sources,
        };
      } else {
        throw new Error(result.error || 'Failed to fetch remote jobs from APIs');
      }
    } catch (error) {
      console.error('Error loading from APIs:', error);
      Alert.alert('Error', `Failed to load jobs: ${error.message}`);
      return { jobs: [], sources: {} };
    }
  };


  /**
   * Function to orchestrate job loading (API + filtering)
   */
  const loadJobs = async () => {
    setIsLoading(true);
    setRefreshing(true);
    
    try {
      const apiResult = await loadJobsFromAPI();
      
      setAllRemoteJobs(apiResult.jobs);
      // Ensure local filtering runs on the new data
      filterJobs(searchQuery, selectedTab, apiResult.jobs); 
      
    } catch (e) {
      // Error handled inside loadJobsFromAPI
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };
  
  
  // Initialize component
  useEffect(() => {
    loadJobs();
    animateEntrance();
  }, []); 

  // Reload when filters change
  useEffect(() => {
    // Re-filter when the global filter state changes
    filterJobs(searchQuery, selectedTab, allRemoteJobs);
  }, [selectedFilters]);


  // Enhanced job filtering for remote-specific criteria
  const filterJobs = (query = '', tab = 'all', jobsList = allRemoteJobs) => {
    let filtered = [...jobsList];

    // Filter by search query
    if (query.trim()) {
      const searchLower = query.toLowerCase();
      filtered = filtered.filter(job =>
        String(job.title ?? '').toLowerCase().includes(searchLower) ||
        String(job.company ?? '').toLowerCase().includes(searchLower) ||
        String(job.location ?? '').toLowerCase().includes(searchLower) ||
        String(job.description ?? '').toLowerCase().includes(searchLower) ||
        (job.requirements && job.requirements.some(req => 
          req.toLowerCase().includes(searchLower)
        )) ||
        (job.remotePerks && Object.values(job.remotePerks).some(perk =>
          String(perk).toLowerCase().includes(searchLower)
        ))
      );
    }

    // Filter by tab
    switch (tab) {
      case 'recommended':
        filtered = filtered.filter(job => job.matchScore >= 85);
        break;
      case 'saved':
        filtered = filtered.filter(job => isJobSaved(job.id));
        break;
      case 'applied':
        filtered = filtered.filter(job => isJobApplied(job.id));
        break;
      case 'recent':
        filtered = filtered.filter(job => 
          job.posted.includes('day') || job.posted.includes('hour')
        );
        break;
    }

    // Apply additional filters
    if (selectedFilters.timeZone !== 'all') {
      filtered = filtered.filter(job => 
        job.remotePerks?.timeZone?.toLowerCase().includes(String(selectedFilters.timeZone ?? '').toLowerCase())
      );
    }

    if (selectedFilters.communication !== 'all') {
      filtered = filtered.filter(job => 
        job.remotePerks?.communication?.toLowerCase().includes(String(selectedFilters.communication ?? '').toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  // Enhanced search handler
  const handleSearch = (query) => {
    setSearchQuery(query);
    filterJobs(query, selectedTab);
  };

  // Tab press handler
  const handleTabPress = (tab) => {
    setSelectedTab(tab.id);
    filterJobs(searchQuery, tab.id);
    
    if (trackUserAction) {
      trackUserAction('remote_jobs_tab_change', { tab: tab.id });
    }
  };

  // Refresh handler
  const onRefresh = async () => {
    setRefreshing(true);
    await loadJobs();
  };

  // Enhanced save job functionality
  const handleSaveJob = async (job) => {
    try {
      const isSaved = isJobSaved(job.id);
      
      if (isSaved) {
        if (unsaveJob && typeof unsaveJob === 'function') {
          await unsaveJob(job.id);
          if (showToast) {
            showToast('Remote job removed from saved jobs', 'info');
          }
        }
      } else {
        if (saveJob && typeof saveJob === 'function') {
          await saveJob(job);
          if (showToast) {
            showToast('Remote job saved successfully!', 'success');
          }
        }
      }
      
      if (trackUserAction) {
        trackUserAction('remote_jobs_save_toggle', { 
          jobId: job.id, 
          action: isSaved ? 'unsave' : 'save' 
        });
      }
    } catch (error) {
      console.error('❌ REMOTE JOBS - Error saving job:', error);
      if (showToast) {
        showToast('Failed to save remote job', 'error');
      }
    }
  };

  // Check if job is saved
  const isJobSaved = (jobId) => {
    return Array.isArray(savedJobs) && savedJobs.some(savedJob => savedJob.id === jobId);
  };

  // Check if job is applied
  const isJobApplied = (jobId) => {
    return Array.isArray(jobApplications) && jobApplications.some(app => app.jobId === jobId);
  };

  // Handle apply to job (kept original logic)
  const handleJobApply = async (job) => {
    try {
      if (!job || !job.id) {
        console.error('❌ REMOTE JOBS - No job data provided for application');
        Alert.alert('Error', 'Job information is not available. Please try again.');
        return;
      }

      console.log('🚀 REMOTE JOBS - Starting application process for:', job.title);
      setApplyingJobId(job.id);

      // Check if already applied
      const isAlreadyApplied = Array.isArray(jobApplications) && 
        jobApplications.some(app => app && (app.jobId === job.id || app.jobId === job.jobId));
      
      if (isAlreadyApplied) {
        Alert.alert('Already Applied', 'You have already applied to this remote position.');
        setApplyingJobId(null);
        return;
      }

      // FIXED: Navigate to SubmitForJobsScreen with comprehensive remote job data
      const navigationParams = {
        jobId: job.id || job.jobId,
        id: job.id || job.jobId,
        job: job,
        jobData: job,
        source: 'RemoteJobsScreen',
        timestamp: new Date().toISOString(),
        jobType: 'Remote',
        remotePerks: job.remotePerks,
        isRemotePosition: true,
      };

      // Check for external apply link (common for remote API jobs)
      const externalLink = job.applyLink || job.jobUrl;
      
      if (externalLink) {
        Alert.alert(
          'Apply Externally',
          'This will open the job application page in your browser.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Continue',
              onPress: async () => {
                try {
                  await Linking.openURL(externalLink);
                  await applyToJob(job.id || job.jobId, {
                    appliedAt: new Date().toISOString(),
                    method: 'external',
                  });
                  showToast?.('Application tracked', 'success');
                } catch (error) {
                  console.error('Error opening apply link:', error);
                  showToast?.('Could not open application link', 'error');
                }
              },
            },
          ]
        );
      } else {
        // Fallback to internal form navigation
        try {
          navigation.navigate('SubmitForJobs', navigationParams);
        } catch (navigationError) {
          console.error('❌ REMOTE JOBS - Navigation to SubmitForJobs failed:', navigationError);
          Alert.alert('Error', 'Unable to open application form. Please try again.');
        }
      }

    } catch (error) {
      console.error('❌ REMOTE JOBS - Error in handleJobApply:', error);
      Alert.alert('Error', 'An error occurred while applying. Please try again.');
    } finally {
      setApplyingJobId(null);
    }
  };


  // Animate entrance
  const animateEntrance = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };


  // Render header
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="arrow-back" size={24} color="#ffffff" />
      </TouchableOpacity>
      
      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>Remote Jobs</Text>
        <Text style={styles.headerSubtitle}>{filteredJobs.length} remote opportunities</Text>
      </View>
      
      <TouchableOpacity
        style={styles.headerActionButton}
        onPress={() => setShowFilters(true)}
      >
        <Ionicons name="options" size={20} color="#ffffff" />
      </TouchableOpacity>
    </View>
  );

  // Render search bar
  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchInputContainer}>
        <Ionicons name="search" size={20} color="#64748B" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search remote jobs, skills, or companies..."
          value={searchQuery}
          onChangeText={handleSearch}
          placeholderTextColor="#94a3b8"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => handleSearch('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // Render remote work benefits
  const renderRemoteBenefits = () => (
    <View style={styles.benefitsContainer}>
      <Text style={styles.benefitsTitle}>Remote Work Benefits</Text>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.benefitsScrollContainer}
      >
        {[
          { icon: 'home', title: 'Work From Home', color: '#4ecdc4' },
          { icon: 'time', title: 'Flexible Hours', color: '#f59e0b' },
          { icon: 'people', title: 'Global Teams', color: '#8b5cf6' },
          { icon: 'car', title: 'No Commute', color: '#10b981' },
        ].map((benefit, index) => (
          <View key={index} style={[styles.benefitCard, { borderColor: benefit.color }]}>
            <View style={[styles.benefitIcon, { backgroundColor: benefit.color }]}>
              <Ionicons name={benefit.icon} size={16} color="#ffffff" />
            </View>
            <Text style={styles.benefitTitle}>{benefit.title}</Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  // Render tabs
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsScrollContainer}
      >
        {remoteTabs.map((tab) => {
          const jobCount = tab.id === 'all' ? filteredJobs.length :
                          tab.id === 'recommended' ? filteredJobs.filter(job => job.matchScore >= 85).length :
                          tab.id === 'saved' ? filteredJobs.filter(job => isJobSaved(job.id)).length :
                          tab.id === 'applied' ? filteredJobs.filter(job => isJobApplied(job.id)).length :
                          tab.id === 'recent' ? filteredJobs.filter(job => job.posted.includes('day')).length : 0;

          return (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => handleTabPress(tab)}
            >
              <Ionicons 
                name={tab.icon} 
                size={16} 
                color={selectedTab === tab.id ? "#ffffff" : "#64748B"} 
              />
              <Text style={[
                styles.tabText,
                selectedTab === tab.id && styles.activeTabText,
              ]}>
                {tab.title}
              </Text>
              {jobCount > 0 && (
                <View style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}>
                  <Text style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}>
                    {jobCount}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  // Render remote job card
  const renderRemoteJobCard = ({ item: job }) => {
    const isSaved = isJobSaved(job.id);
    const isApplied = isJobApplied(job.id);
    const isApplying = applyingJobId === job.id;

    return (
      <Animated.View 
        style={[
          styles.jobCard,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        {/* Job badges */}
        <View style={styles.jobBadges}>
          <View style={[styles.badge, styles.remoteBadge]}>
            <Ionicons name="home" size={12} color="#ffffff" />
            <Text style={styles.badgeText}>100% Remote</Text>
          </View>
          {job.featured && (
            <View style={[styles.badge, styles.featuredBadge]}>
              <Ionicons name="star" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Featured</Text>
            </View>
          )}
          {job.urgent && (
            <View style={[styles.badge, styles.urgentBadge]}>
              <Ionicons name="flash" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Urgent</Text>
            </View>
          )}
          {job.companyInfo?.remoteFirst && (
            <View style={[styles.badge, styles.remoteFirstBadge]}>
              <Ionicons name="globe" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Remote First</Text>
            </View>
          )}
        </View>

        {/* Company info */}
        <View style={styles.jobHeader}>
          <View style={styles.companyLogoContainer}>
            {job.companyLogo ? (
              <Image 
                source={{ uri: (job.companyLogo && typeof job.companyLogo=="string" && job.companyLogo.startsWith("http")) ? job.companyLogo : undefined }} 
                style={styles.companyLogo}
                onError={() => console.warn('Failed to load company logo for:', job.company)}
              />
            ) : (
              <View style={styles.companyLogoPlaceholder}>
                <Text style={styles.companyLogoText}>
                  {job.company.charAt(0)}
                </Text>
              </View>
            )}
            
            {/* Remote indicator overlay */}
            <View style={styles.remoteIndicator}>
              <Ionicons name="home" size={12} color="#ffffff" />
            </View>
          </View>

          <View style={styles.jobInfo}>
            <Text style={styles.jobTitle} numberOfLines={2}>{job.title}</Text>
            <Text style={styles.companyName}>{job.company}</Text>
            
            <View style={styles.jobMeta}>
              <View style={styles.metaItem}>
                <Ionicons name="globe" size={12} color="#64748B" />
                <Text style={styles.metaText}>{job.location}</Text>
              </View>
              <View style={styles.metaItem}>
                <Ionicons name="cash" size={12} color="#64748B" />
                <Text style={styles.metaText}>{job.salary}</Text>
              </View>
            </View>
          </View>

          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => handleSaveJob(job)}
          >
            <Ionicons 
              name={isSaved ? "bookmark" : "bookmark-outline"} 
              size={20} 
              color={isSaved ? "#f59e0b" : "#64748B"} 
            />
          </TouchableOpacity>
        </View>
        
        {/* Job description */}
        <Text style={styles.jobDescription} numberOfLines={2}>
          {job.description}
        </Text>

        {/* Remote perks */}
        <View style={styles.remotePerksContainer}>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.remotePerksScrollContainer}
          >
            {job.remotePerks && Object.entries(job.remotePerks).slice(0, 3).map(([key, value], index) => (
              <View key={index} style={styles.remotePerkTag}>
                <Text style={styles.remotePerkText}>{value}</Text>
              </View>
            ))}
          </ScrollView>
        </View>
        
        {/* Match score and actions */}
        <View style={styles.jobFooter}>
          <View style={styles.matchContainer}>
            <View style={styles.matchScore}>
              <Text style={styles.matchText}>{job.matchScore}% match</Text>
            </View>
            <Text style={styles.postedTime}>{job.posted}</Text>
          </View>

          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.detailsButton}
              onPress={() => {
                try {
                  navigation.navigate('JobDetails', { 
                    jobId: job.id,
                    id: job.id,
                    job: job,
                    source: 'RemoteJobsScreen'
                  });
                } catch (error) {
                  console.error('❌ REMOTE JOBS - Navigation to JobDetailsScreen failed:', error);
                  Alert.alert('Error', 'Unable to view job details. Please try again.');
                }
              }}
            >
              <Ionicons name="information-circle-outline" size={16} color="#4ecdc4" />
              <Text style={styles.detailsButtonText}>Details</Text>
            </TouchableOpacity>

            {/* Apply Now button with proper navigation */}
            <TouchableOpacity
              style={[
                styles.applyButton,
                isApplied && styles.appliedButton,
                isApplying && styles.applyingButton,
              ]}
              onPress={() => handleJobApply(job)}
              disabled={isApplied || isApplying}
            >
              {isApplying ? (
                <>
                  <ActivityIndicator size={20} color="#ffffff" />
                  <Text style={styles.applyButtonText}>Applying...</Text>
                </>
              ) : (
                <>
                  {isApplied ? (
                    <Ionicons name="checkmark-circle" size={16} color="#ffffff" />
                  ) : (
                    <Ionicons name="paper-plane" size={16} color="#ffffff" />
                  )}
                <Text style={[styles.applyButtonText, isApplied && styles.appliedButtonText]}>
                  {isApplied ? 'Applied' : 'Apply Now'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Animated.View>
  );
};

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="home-outline" size={64} color="#94a3b8" />
      <Text style={styles.emptyTitle}>No Remote Jobs Found</Text>
      <Text style={styles.emptyMessage}>
        Try adjusting your search criteria or check back later for new remote opportunities.
      </Text>
      <TouchableOpacity
        style={styles.refreshButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedTab('all');
          filterJobs('', 'all');
        }}
      >
        <Ionicons name="refresh" size={16} color="#4ecdc4" />
        <Text style={styles.refreshButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  // Render filter modal
  const renderFilterModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowFilters(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Remote Job Filters</Text>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowFilters(false)}
          >
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalContent}>
          {/* Time Zone Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Time Zone Preference</Text>
            <View style={styles.filterOptions}>
              {['all', 'flexible', 'us', 'eu', 'asia'].map((zone) => (
                <TouchableOpacity
                  key={zone}
                  style={[
                    styles.filterOption,
                    selectedFilters.timeZone === zone && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, timeZone: zone }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.timeZone === zone && styles.selectedFilterOptionText,
                  ]}>
                    {zone.charAt(0).toUpperCase() + zone.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Communication Style Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Communication Style</Text>
            <View style={styles.filterOptions}>
              {['all', 'async', 'sync', 'mixed'].map((style) => (
                <TouchableOpacity
                  key={style}
                  style={[
                    styles.filterOption,
                    selectedFilters.communication === style && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, communication: style }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.communication === style && styles.selectedFilterOptionText,
                  ]}>
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Apply Filters Button */}
          <TouchableOpacity
            style={styles.applyFiltersButton}
            onPress={() => {
              filterJobs(searchQuery, selectedTab);
              setShowFilters(false);
            }}
          >
            <Text style={styles.applyFiltersButtonText}>Apply Filters</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4ecdc4" />

      {renderHeader()}

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4ecdc4" />
          <Text style={styles.loadingText}>Loading Remote Engineering Jobs...</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#4ecdc4" />
          }
        >
          {renderSearchBar()}
          {renderRemoteBenefits()}
          {renderTabs()}

          <View style={styles.jobsContainer}>
            <Text style={styles.jobsTitle}>
              {remoteTabs.find(tab => tab.id === selectedTab)?.title || 'Remote Jobs'} ({filteredJobs.length})
            </Text>
            
            {filteredJobs.length > 0 ? (
              <FlatList
                data={filteredJobs}
                renderItem={renderRemoteJobCard}
                keyExtractor={(item) => item.id}
                scrollEnabled={false}
                showsVerticalScrollIndicator={false}
              />
            ) : (
              renderEmptyState()
            )}
          </View>
        </ScrollView>
      )}

      {renderFilterModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4ecdc4',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#4ecdc4',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '500',
  },
  headerActionButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  content: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  searchContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1e293b',
    marginLeft: 12,
  },
  benefitsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  benefitsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
    paddingHorizontal: 20,
  },
  benefitsScrollContainer: {
    paddingHorizontal: 20,
  },
  benefitCard: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    marginRight: 12,
    borderWidth: 2,
    minWidth: 80,
  },
  benefitIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  benefitTitle: {
    fontSize: 11,
    color: '#1e293b',
    fontWeight: '600',
    textAlign: 'center',
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabsScrollContainer: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  activeTab: {
    backgroundColor: '#4ecdc4',
    borderColor: '#4ecdc4',
  },
  tabText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabBadge: {
    backgroundColor: '#e2e8f0',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  activeTabBadge: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  tabBadgeText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '600',
  },
  activeTabBadgeText: {
    color: '#ffffff',
  },
  jobsContainer: {
    backgroundColor: '#ffffff',
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  jobsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
  },
  jobCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  jobBadges: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
  },
  remoteBadge: {
    backgroundColor: '#4ecdc4',
  },
  featuredBadge: {
    backgroundColor: '#f59e0b',
  },
  urgentBadge: {
    backgroundColor: '#ef4444',
  },
  remoteFirstBadge: {
    backgroundColor: '#8b5cf6',
  },
  badgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  jobHeader: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  companyLogoContainer: {
    position: 'relative',
    marginRight: 12,
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#f1f5f9',
  },
  companyLogoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#4ecdc4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  companyLogoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  remoteIndicator: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#10b981',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 4,
    lineHeight: 22,
  },
  companyName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4ecdc4',
    marginBottom: 8,
  },
  jobMeta: {
    flexDirection: 'row',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
    fontWeight: '500',
  },
  saveButton: {
    padding: 8,
  },
  jobDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
  },
  remotePerksContainer: {
    marginBottom: 16,
  },
  remotePerksScrollContainer: {
    paddingRight: 20,
  },
  remotePerkTag: {
    backgroundColor: '#f0fdfa',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#a7f3d0',
  },
  remotePerkText: {
    fontSize: 12,
    color: '#065f46',
    fontWeight: '500',
  },
  jobFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  matchContainer: {
    flex: 1,
  },
  matchScore: {
    backgroundColor: '#dcfce7',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  matchText: {
    fontSize: 12,
    color: '#166534',
    fontWeight: '600',
  },
  postedTime: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: '500',
  },
  actionButtons: {
    flexDirection: 'row',
  },
  detailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdfa',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#a7f3d0',
  },
  detailsButtonText: {
    fontSize: 12,
    color: '#4ecdc4',
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#4ecdc4',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
  },
  appliedButton: {
    backgroundColor: '#10b981',
  },
  applyingButton: {
    backgroundColor: '#94a3b8',
  },
  applyButtonText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  appliedButtonText: {
    color: '#ffffff',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0fdfa',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#a7f3d0',
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#4ecdc4',
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
  },
  modalCloseButton: {
    padding: 8,
  },
  modalContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 20,
  },
  filterTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterOption: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 8,
    marginBottom: 8,
  },
  selectedFilterOption: {
    backgroundColor: '#4ecdc4',
    borderColor: '#4ecdc4',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  selectedFilterOptionText: {
    color: '#ffffff',
  },
  applyFiltersButton: {
    backgroundColor: '#4ecdc4',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginVertical: 20,
  },
  applyFiltersButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
});